import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { loadAssetChildNameCounts } from '@/itemFinder/itemFinder.api';
import { ASSET_LEAF_CHILDREN_PAGE_SIZE } from '@/itemFinder/itemFinder.constants';
import { GetAssetChildrenByNameQueryKey } from '@seeqdev/api/endpoint/ItemsApi';
import type { AssetLeafChildName } from '@/itemFinder/itemFinder.constants';

/**
 * Asset leaf child names change rarely relative to UI activity within a single edit session; a
 * modest staleTime lets re-selecting a previously-viewed asset/type/filter combination (e.g.
 * reopening a saved item finder, or toggling the type multiselect back) serve from cache instead
 * of re-querying. Mirrors NODE_OPTIONS_STALE_TIME_MS in tableDefinitionEditor/nodeOptions.queries.ts.
 */
const ASSET_LEAF_CHILDREN_STALE_TIME_MS = 60_000;

interface AssetLeafChildNamesPage {
  items: AssetLeafChildName[];
  totalResults: number;
  /** Distinct-name count loaded so far (across this and all prior pages); doubles as the next offset. */
  loadedCount: number;
}

async function fetchAssetLeafChildNamesPage(
  asset: string,
  types: string[],
  filter: string,
  offset: number,
  limit: number,
): Promise<AssetLeafChildNamesPage> {
  const { items, totalResults } = await loadAssetChildNameCounts(asset, types, filter, offset, limit);
  return { items, totalResults, loadedCount: offset + items.length };
}

/**
 * Paginated, cached query for the leaf-descendant names (grouped, with counts) of `asset`, backing
 * the Tree Builder "asset leaf children" search. Mirrors `useDescendantOptions` in
 * `tableDefinitionEditor/nodeOptions.queries.ts`: always loads the first page; call
 * `fetchNextPage()` for more (e.g. on infinite scroll).
 *
 * Disabled (no request fired) until both `asset` and `types` are populated, matching the previous
 * imperative gating (`if (ancestorId && types.length > 0)`) that guarded every call site.
 */
function useAssetLeafChildNamesInfiniteQuery(asset: string | undefined, types: string[], filter: string) {
  // Sort so type-selection order doesn't fragment the cache into equivalent-but-differently-keyed entries.
  const sortedTypes = [...types].sort();
  return useInfiniteQuery({
    queryKey: [
      ...GetAssetChildrenByNameQueryKey({ asset: asset ?? '', types: sortedTypes, filter: filter || undefined }),
      'infinite',
    ],
    queryFn: ({ pageParam }) =>
      fetchAssetLeafChildNamesPage(asset ?? '', sortedTypes, filter, pageParam, ASSET_LEAF_CHILDREN_PAGE_SIZE),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.loadedCount < lastPage.totalResults ? lastPage.loadedCount : undefined),
    enabled: !!asset && types.length > 0,
    staleTime: ASSET_LEAF_CHILDREN_STALE_TIME_MS,
    // Retain the previously rendered names while a new filter (or asset/types) keys a different
    // cache entry, so the checkbox list doesn't blank-flicker as the user types.
    placeholderData: keepPreviousData,
  });
}

export interface AssetLeafChildNamesResult {
  childNames: AssetLeafChildName[];
  /** True while any fetch (initial load, next page, or a refetch after asset/types/filter changes) is in flight. */
  isLoading: boolean;
  hasMore: boolean;
  /** Safe to wire directly into `InfiniteScroll`'s `loadMore` — no-ops if already fetching or exhausted. */
  loadMore: () => void;
}

/**
 * Flattens the paginated query into the shape `AssetLeafChildrenSelectFormComponent` renders
 * directly. Consolidates what used to be 5 separate imperative call sites (asset/types/filter
 * `onChange` handlers, an infinite-scroll "load more" callback, and an `ItemFinder.organism.tsx`
 * hydration `useEffect`) into a single declarative fetch keyed on `(asset, types, filter)`.
 */
export function useAssetLeafChildNames(
  asset: string | undefined,
  types: string[],
  filter: string,
): AssetLeafChildNamesResult {
  const query = useAssetLeafChildNamesInfiniteQuery(asset, types, filter);

  const childNames = (query.data?.pages ?? []).flatMap((page) => page.items);

  const loadMore = () => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage();
    }
  };

  return {
    childNames,
    isLoading: query.isFetching,
    hasMore: !!query.hasNextPage,
    loadMore,
  };
}
